/**
 * Utility functions for reading cookies client-side.
 * Used to pass data from Next.js middleware to client-side code.
 */

import type { PostHogFlagsResult } from '@/lib/posthogServerFlags';
import * as Sentry from '@sentry/nextjs';

/** Written by middleware.ts with the flags it evaluated for the request. */
export const BOOTSTRAP_FLAGS_COOKIE = 'ph_bootstrap_flags';

/**
 * Gets a cookie value by name.
 * Returns null if the cookie doesn't exist.
 */
export function getCookie(name: string): string | null {
	if (typeof document === 'undefined') {
		return null;
	}

	// Leading separator so the first cookie is matched the same way as the rest.
	const cookieHeader = `; ${document.cookie}`;
	const separator = `; ${name}=`;
	const segmentsAfterName = cookieHeader.split(separator);

	// A name can legitimately appear twice — posthog-js scopes its cookie to `.apex` while
	// middleware sets a host-only one — so read the first match rather than giving up. Bailing here
	// silently dropped the bootstrapped distinct_id for anyone holding both.
	const hasMatch = segmentsAfterName.length >= 2;
	if (hasMatch) {
		const firstMatch = segmentsAfterName[1];
		const cookieValue = firstMatch?.split(';')[0];
		if (!cookieValue) return null;
		try {
			return decodeURIComponent(cookieValue);
		} catch (error) {
			// Cookie value may contain raw % or malformed encoding; return as-is
			Sentry.withScope((scope) => {
				scope.setTag('module', 'cookies');
				scope.setTag('errorType', 'decode');
				scope.setLevel('warning');
				scope.setContext('cookie', { name, valueLength: cookieValue.length });
				Sentry.captureException(error);
			});
			return cookieValue;
		}
	}

	return null;
}

/**
 * Parses a JSON cookie value.
 * Returns null if parsing fails or cookie doesn't exist.
 */
export function getJSONCookie<T>(name: string): T | null {
	const cookieValue = getCookie(name);
	if (!cookieValue) {
		return null;
	}

	try {
		return JSON.parse(cookieValue) as T;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn(`Failed to parse cookie "${name}":`, error);
		Sentry.withScope((scope) => {
			scope.setTag('module', 'cookies');
			scope.setTag('errorType', 'json-parse');
			scope.setLevel('warning');
			scope.setContext('cookie', {
				name,
				valueLength: cookieValue.length,
				valuePreview: cookieValue.slice(0, 100),
			});
			Sentry.captureException(error);
		});
		return null;
	}
}

/** Written by middleware.ts with the split-url arms whose test the served page belongs to. */
export const SPLIT_URL_EXPOSURE_COOKIE = 'ph_split_url_exposure';

/** Split-url arms this page records as exposures; null when the middleware left no cookie. */
export function readSplitUrlExposureArms(): PostHogFlagsResult['flags'] | null {
	return getJSONCookie<PostHogFlagsResult['flags']>(SPLIT_URL_EXPOSURE_COOKIE);
}

/** Flags the middleware evaluated for this page; null when it left no cookie. */
export function readBootstrappedFlags(): PostHogFlagsResult['flags'] | null {
	return getJSONCookie<PostHogFlagsResult['flags']>(BOOTSTRAP_FLAGS_COOKIE);
}

/**
 * Name of posthog-js's own persistence cookie, with the same character sanitization posthog-js
 * applies to the project key. Shared by the middleware that seeds it and the client that reads it —
 * the two must agree byte-for-byte or the bootstrapped distinct_id is silently lost.
 * Returns null when the key is unset.
 */
export function getPostHogPersistenceCookieName(): string | null {
	const projectKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
	if (!projectKey) return null;

	const sanitizedKey = projectKey.replace(/\+/g, 'PL').replace(/\//g, 'SL').replace(/=/g, 'EQ');
	return `ph_${sanitizedKey}_posthog`;
}

/**
 * The visitor's distinct_id from posthog-js's persistence cookie, which middleware seeds for new
 * visitors with the id it evaluated their flags on. Undefined when the key is unset, the cookie is
 * missing or malformed, or it holds no usable id.
 */
export function readPostHogDistinctId(): string | undefined {
	const cookieName = getPostHogPersistenceCookieName();
	if (!cookieName) return undefined;

	const distinctId = getJSONCookie<{ distinct_id?: unknown }>(cookieName)?.distinct_id;
	return typeof distinctId === 'string' && distinctId ? distinctId : undefined;
}
